import { useMemo } from "react";
import { ArrowLeft, AlertTriangle, Clock, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { MyTruckRow } from "@/hooks/useMyTrucks";

interface TruckExpirationPaneProps {
  onBack: () => void;
  trucks: MyTruckRow[];
}

interface BucketedTruck {
  truck: MyTruckRow;
  hoursUntil: number;
}

/**
 * Carrier-side truck expiration alerts surface.
 * Reads from the already-mounted useMyTrucks() data, buckets by 24h / 48h / 7d
 * windows, and offers a one-click repost via /post-truck?duplicate=<id>.
 */
export function TruckExpirationPane({ onBack, trucks }: TruckExpirationPaneProps) {
  const navigate = useNavigate();

  const { within24, within48, within7d } = useMemo(() => {
    const now = Date.now();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const buckets: { within24: BucketedTruck[]; within48: BucketedTruck[]; within7d: BucketedTruck[] } = {
      within24: [],
      within48: [],
      within7d: [],
    };

    for (const t of trucks) {
      if (t.status === "expired" || t.status === "private") continue;
      if (!t.availableTo) continue;
      const exp = new Date(t.availableTo);
      exp.setHours(23, 59, 59, 999); // end-of-day
      const ms = exp.getTime() - now;
      if (ms < 0) continue;
      const hrs = ms / (1000 * 60 * 60);
      const item: BucketedTruck = { truck: t, hoursUntil: hrs };
      if (hrs <= 24) buckets.within24.push(item);
      else if (hrs <= 48) buckets.within48.push(item);
      else if (hrs <= 24 * 7) buckets.within7d.push(item);
    }

    const sorter = (a: BucketedTruck, b: BucketedTruck) => a.hoursUntil - b.hoursUntil;
    buckets.within24.sort(sorter);
    buckets.within48.sort(sorter);
    buckets.within7d.sort(sorter);
    return buckets;
  }, [trucks]);

  const total = within24.length + within48.length + within7d.length;

  return (
    <div className="flex flex-col h-full rounded-lg border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-sm)" }}>
      <div className="flex items-center gap-2 border-b px-3 py-2.5 bg-muted/20">
        <button onClick={onBack} className="p-1 rounded hover:bg-muted transition-colors">
          <ArrowLeft className="h-4 w-4 text-muted-foreground" />
        </button>
        <AlertTriangle className="h-4 w-4 text-warning" />
        <h4 className="text-caption font-bold text-foreground">Truck Expiration Alerts</h4>
        {within24.length > 0 && (
          <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-warning/15 text-warning animate-pulse">
            {within24.length} dropping in 24h
          </span>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        {total === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-2">
            <Clock className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No trucks expiring soon.</p>
            <p className="text-xs text-muted-foreground/70">
              We'll flag any of your active trucks scheduled to drop off the board within 7 days.
            </p>
          </div>
        ) : (
          <div>
            {within24.length > 0 && (
              <ExpirationGroup
                label="Drops off within 24 hours"
                tone="warning"
                items={within24}
                onRepost={(id) => navigate(`/post-truck?duplicate=${id}`)}
              />
            )}
            {within48.length > 0 && (
              <ExpirationGroup
                label="Drops off within 48 hours"
                tone="muted"
                items={within48}
                onRepost={(id) => navigate(`/post-truck?duplicate=${id}`)}
              />
            )}
            {within7d.length > 0 && (
              <ExpirationGroup
                label="Drops off this week"
                tone="muted"
                items={within7d}
                onRepost={(id) => navigate(`/post-truck?duplicate=${id}`)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ExpirationGroup({
  label,
  tone,
  items,
  onRepost,
}: {
  label: string;
  tone: "warning" | "muted";
  items: BucketedTruck[];
  onRepost: (id: string) => void;
}) {
  const labelClass = tone === "warning" ? "text-warning" : "text-muted-foreground";
  return (
    <div>
      <div className="px-3 py-2 flex items-center gap-1.5">
        <Clock className={`h-3.5 w-3.5 ${labelClass}`} />
        <span className={`text-[11px] font-bold uppercase tracking-wider ${labelClass}`}>{label}</span>
      </div>
      <div className="grid grid-cols-[1fr_80px_70px_90px] items-center px-3 py-1 border-b border-border/50">
        <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Lane</span>
        <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Equipment</span>
        <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Expires</span>
        <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Action</span>
      </div>
      {items.map(({ truck, hoursUntil }, i) => (
        <div
          key={truck.id}
          className={`grid grid-cols-[1fr_80px_70px_90px] items-center px-3 py-2 border-b border-border/30 hover:bg-muted/30 transition-colors ${i % 2 === 1 ? "bg-muted/20" : ""}`}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
              <span className="truncate">{truck.origin}</span>
              <span className="text-muted-foreground">→</span>
              <span className="truncate">{truck.destination}</span>
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              Ref {truck.ref} · Posted {truck.ageHours}h ago
            </div>
          </div>
          <span className="text-[10px] font-medium text-foreground/70 text-right truncate">
            {truck.equipmentType}
          </span>
          <span className={`text-[10px] font-semibold tabular-nums text-right ${hoursUntil <= 24 ? "text-warning" : "text-muted-foreground"}`}>
            {formatHours(hoursUntil)}
          </span>
          <div className="flex justify-end">
            <Button
              size="sm"
              variant={hoursUntil <= 24 ? "default" : "outline"}
              className="h-6 text-[10px] px-2.5 flex-shrink-0"
              onClick={() => onRepost(truck.id)}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Repost
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function formatHours(h: number): string {
  if (h < 1) return "<1h";
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}
